import { useState } from "react";
import clsx from "clsx";
import { formatBudgetDays, type EmployeeAbsenceBudget } from "@/lib/employeeAbsences";

interface AbsenceBudgetOverviewProps {
  year: number;
  budget: EmployeeAbsenceBudget | null;
  heroLinked: boolean;
  loading?: boolean;
}

const OTHER_TYPES: Array<{ usedKey: keyof EmployeeAbsenceBudget; label: string }> = [
  { usedKey: "used_overtime_reduction_days", label: "Überstundenabbau" },
  { usedKey: "used_sick_days", label: "Krankheit" },
  { usedKey: "used_child_sick_days", label: "Kindkrank" },
  { usedKey: "used_special_leave_days", label: "Sonderurlaub" },
  { usedKey: "used_training_days", label: "Schulung" },
  { usedKey: "used_unpaid_days", label: "Unbezahlt" },
];

function Stat({
  label,
  value,
  hint,
  accent = false,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: boolean;
}) {
  return (
    <div
      className={clsx(
        "rounded-xl px-3 py-3",
        accent ? "bg-accent/10" : "bg-fill-quaternary/60",
      )}
    >
      <div className="text-[11px] font-medium uppercase tracking-wide text-label-tertiary">
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-1">
        <span
          className={clsx(
            "text-2xl font-semibold tabular-nums leading-none",
            accent ? "text-accent" : "text-label",
          )}
        >
          {value}
        </span>
        <span className="text-xs text-label-tertiary">Tage</span>
      </div>
      {hint ? <div className="mt-1 text-xs text-label-secondary">{hint}</div> : null}
    </div>
  );
}

export function AbsenceBudgetOverview({
  year,
  budget,
  heroLinked,
  loading = false,
}: AbsenceBudgetOverviewProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  if (loading && !budget) {
    return (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" aria-hidden>
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="h-[4.75rem] animate-pulse rounded-xl bg-fill-tertiary/70" />
        ))}
      </div>
    );
  }

  const otherLines = budget
    ? OTHER_TYPES.map((item) => ({
        label: item.label,
        days: Number(budget[item.usedKey] ?? 0) || 0,
      })).filter((line) => line.days > 0)
    : [];

  const available = budget?.available_vacation_days ?? budget?.remaining_vacation_days ?? 0;
  const pending = budget?.pending_vacation_days ?? 0;
  const claim = budget?.vacation_days ?? 0;
  const carryover = budget?.vacation_carryover_days ?? 0;
  const total = budget?.total_vacation_budget ?? claim + carryover;
  const used = budget?.used_vacation_days ?? 0;

  return (
    <section className="rounded-ios border border-separator bg-card p-4 shadow-ios">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-label">Urlaub {year}</h2>
        <button
          type="button"
          className="text-xs font-medium text-accent hover:underline"
          aria-expanded={detailsOpen}
          onClick={() => setDetailsOpen((open) => !open)}
        >
          {detailsOpen ? "Details ausblenden" : "Budget-Details"}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Verfügbar" value={formatBudgetDays(available)} accent />
        <Stat label="Beantragt" value={formatBudgetDays(pending)} />
        <Stat label="Genommen" value={formatBudgetDays(used)} />
        <Stat
          label="Anspruch"
          value={formatBudgetDays(total)}
          hint={`${formatBudgetDays(claim)} + ${formatBudgetDays(carryover)} Rest`}
        />
      </div>

      {otherLines.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {otherLines.map((line) => (
            <span
              key={line.label}
              className="rounded-full bg-fill-tertiary px-2.5 py-1 text-xs text-label-secondary"
            >
              {line.label} · {formatBudgetDays(line.days)} Tage
            </span>
          ))}
        </div>
      ) : null}

      {detailsOpen ? (
        <dl className="mt-3 grid gap-2 border-t border-separator/70 pt-3 text-sm sm:grid-cols-2">
          <div className="flex justify-between gap-3">
            <dt className="text-label-secondary">Jahresanspruch</dt>
            <dd className="tabular-nums text-label">{formatBudgetDays(claim)} Tage</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-label-secondary">
              Resturlaub Vorjahr
              {budget?.vacation_carryover_manual ? " (manuell)" : " (auto)"}
            </dt>
            <dd className="tabular-nums text-label">{formatBudgetDays(carryover)} Tage</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-label-secondary">Gesamtbudget</dt>
            <dd className="font-semibold tabular-nums text-label">{formatBudgetDays(total)} Tage</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-label-secondary">genommen / genehmigt</dt>
            <dd className="tabular-nums text-label">{formatBudgetDays(used)} Tage</dd>
          </div>
        </dl>
      ) : null}

      {heroLinked ? (
        <p className="mt-3 text-[11px] text-label-tertiary">
          Genommen zählt App und Hero zusammen. Überlappende Tage nur einmal, ohne Wochenenden
          und Feiertage.
        </p>
      ) : null}
    </section>
  );
}
